import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Row, Col, Card, Nav, Tab } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { teamApi } from '../../api';

import { DEFAULT_ADDRESS, DEFAULT_PHONE, DEFAULT_IDENTITY, DEFAULT_EMAIL } from '../../constants';
import { hasPermissions } from '../../utils';

import Loader from '../../components/common/Loader';
import PageTitle from '../../components/common/PageTitle';
import EndUserForm from '../../components/common/endusers/EndUserForm';
import ConfirmModal from '../../components/common/ConfirmModal';
import FreezeModal from '../../components/common/FreezeModal';
import PaymentMethods from '../../components/common/endusers/PaymentMethods';

function EndUserProfile({ t, setAlert, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { handle, action, pi_id } = useParams();
  const [key, setKey] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [confirm, setConfirm] = useState({ show: false, data: undefined });
  const [redirect, setRedirect] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    handle: '',
    identities: DEFAULT_IDENTITY,
    birthdate: '',
    emails: DEFAULT_EMAIL,
    phones: DEFAULT_PHONE,
    addresses: DEFAULT_ADDRESS,
  });
  const [name, setName] = useState(`${initialValues.first_name} ${initialValues.last_name}`);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const formRef = useRef(null);

  const getEndUser = async (callback) => {
    try {
      const endUserResponse = await teamApi.getEndUser(handle, user.currentTeam.id);
      if (endUserResponse.success) {
        const full_name = endUserResponse.first_name
          ? `${endUserResponse.first_name} ${endUserResponse.last_name}`
          : endUserResponse.full_name;

        const data = {
          first_name: endUserResponse.first_name,
          last_name: endUserResponse.last_name,
          full_name: full_name,
          handle: endUserResponse.user_handle,
          identities: [{ identity: endUserResponse.ssn }],
          birthdate: endUserResponse.birthdate,
          emails: [endUserResponse.email],
          phones: [endUserResponse.phone],
          addresses: [endUserResponse.address],
          entity_type: endUserResponse.entity_type || 'individual',
        };
        if (endUserResponse.ein) {
          data.identities = [{ identity: endUserResponse.ein }];
        }
        if (endUserResponse.can_unfreeze) data.canUnfreeze = true;

        setName(full_name);
        setInitialValues(data);
        if (callback) callback(data);
      } else {
        setRedirect(true);
      }
    } catch (error) {
      setAlert({ message: error, variant: 'danger' });
      setRedirect(true);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (values) => {
    if (values) {
      try {
        // eslint-disable-next-line no-shadow
        const { phones, emails, addresses, handle, ...newValues } = values;
        await teamApi.updateEndUser(
          initialValues.handle,
          {
            ...newValues,
            user_handle: handle,
            phone: phones[0],
            email: emails[0],
            address: addresses[0],
          },
          user.currentTeam.id
        );
        setAlert({
          message: t(`admin.user.update.message`, { name: values.full_name }),
          variant: 'success',
        });
      } catch (error) {
        setAlert({ message: error, variant: 'danger' });
        formRef.current.resetForm({ value: initialValues });
      } finally {
        setConfirm({ show: false, data: undefined });
        getEndUser();
      }
    } else {
      formRef.current.setSubmitting(false);
      setConfirm({ show: false, data: undefined });
    }
  };

  const unfreezeUser = async () => {
    try {
      await teamApi.unfreezeEndUser(initialValues.handle, user.currentTeam.id);
      setAlert({
        message: t(`admin.user.update.message`, { name: initialValues.full_name }),
        variant: 'success',
      });
    } catch (error) {
      setAlert({ message: error, variant: 'danger' });
      formRef.current.resetForm({ value: initialValues });
    } finally {
      setConfirm({ show: false, data: undefined });
      getEndUser();
    }
  };

  useEffect(() => {
    setKey(action);
  }, [action]);

  useEffect(() => {
    if (redirect) navigate('/settings/end_users');
  }, [redirect]);

  useEffect(() => {
    getEndUser();
  }, []);

  return (
    <Container className="main-content-container p-3 p-md-5 d-flex flex-column flex-grow-1">
      <Row className="g-0 page-header mb-3 mb-md-5">
        <PageTitle title={t('admin.endUsers.profile.title')} backBtn />
      </Row>

      {isLoading ? (
        <Loader />
      ) : (
        <Card className="mb-3 mb-md-5 loaded">
          <Card.Header className="d-flex justify-content-between align-items-center space p-4">
            <h3 className="m-0">
              <i className={`sila-icon me-2 text-primary ${initialValues.entity_type}`} />
              {name}
            </h3>
            {initialValues.canUnfreeze && hasPermissions(user, 'end_users', 'Write') && (
              <Button
                onClick={() => setShowFreezeModal(true)}
                className="generate me-0 rounded-1 py-1"
                variant="primary"
                size="sm"
              >
                {t(`common:buttons.unfreeze`)}
              </Button>
            )}
          </Card.Header>
          <Card.Body as={Row} className="p-0 g-0">
            <Tab.Container activeKey={key} defaultActiveKey={key} onSelect={(k) => setKey(k)}>
              <Col md="4" lg="3" xl="2" className="border-end">
                <Nav className="d-flex flex-column align-content-start h-100 py-4">
                  <Nav.Item className="w-100">
                    <Nav.Link
                      className={`d-block px-4 py-3${
                        key === 'profile' ? ' bg-primary-accent text-primary' : ' text-info'
                      }`}
                      eventKey="profile"
                      as={NavLink}
                      to={{
                        pathname: `/settings/end_user/${handle}/profile`,
                        state: { from: location.pathname },
                      }}
                    >
                      {t('admin.endUsers.navigation.profile')}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item className="w-100">
                    <Nav.Link
                      className={`d-block px-4 py-3${
                        key === 'payment-instruments'
                          ? ' bg-primary-accent text-primary'
                          : ' text-info'
                      }`}
                      eventKey="payment-instruments"
                      as={NavLink}
                      to={{
                        pathname: `/settings/end_user/${handle}/payment-instruments`,
                        state: { from: location.pathname },
                      }}
                    >
                      {t('admin.endUsers.navigation.paymentInstruments')}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col md="8" lg="9" xl="10" className="p-4">
                <Tab.Content>
                  <Tab.Pane eventKey="profile">
                    <EndUserForm
                      customer
                      formRef={formRef}
                      initialValues={initialValues}
                      setName={setName}
                      onSubmit={(values) => setConfirm({ ...confirm, show: true, data: values })}
                      // eslint-disable-next-line react/no-unstable-nested-components
                      formFooterComponent={({ values }) => (
                        <Button
                          variant="outline-light"
                          className="ms-4"
                          onClick={() =>
                            navigate(`/settings/transactions?user_handle=${values.handle}`)
                          }
                        >
                          {t('common:buttons.viewTransactions')}
                        </Button>
                      )}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="payment-instruments">
                    <PaymentMethods
                      handle={initialValues.handle}
                      getEndUser={getEndUser}
                      name={
                        initialValues.full_name ||
                        `${initialValues.first_name} ${initialValues.last_name}`
                      }
                      redirect={() => setRedirect(true)}
                      piUuid={pi_id}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Tab.Container>
          </Card.Body>
        </Card>
      )}

      {confirm.show && (
        <ConfirmModal
          show={confirm.show}
          data={confirm.data}
          title={t('settings.endUsers.user.confirm.title')}
          renderMessageComponent={() => (
            <p className="text-lg text-info">
              {t(`settings.endUsers.user.confirm.description`, {
                name: `${confirm.data.full_name}`,
              })}
            </p>
          )}
          buttonLabel={t('settings.endUsers.user.confirm.action')}
          onHide={updateUser}
        />
      )}

      {showFreezeModal && (
        <FreezeModal
          show={showFreezeModal}
          data={{}}
          handleFreeze={unfreezeUser}
          setShow={setShowFreezeModal}
          loaderButtonText={t('common:buttons.unfreeze')}
          isAdmin={false}
          customDescription={t('common:freezeUnfreeze.modal.unFreeze.confirm', {
            name: initialValues.full_name,
          })}
          customTitle={t('common:freezeUnfreeze.modal.unFreeze.title', {
            type: t('common:endUsers.labels.singular'),
          })}
        />
      )}
    </Container>
  );
}

export default EndUserProfile;
